import { INTERACTIONS_PLUGIN, type FaitInteraction } from '@belvdr/shared'
import { BLOCK_CLASS } from './masking.js'
import {
  CANDIDATS_NOTABLES,
  elementActionnable,
  estNotable,
  notableLePlusProche,
  signatureDe,
} from './signature.js'

/**
 * Plugin rrweb des Faits (voir `CONTEXT.md`) : relève les Actions du visiteur (clic sur un élément
 * actionnable, envoi d'un formulaire), les Apparitions et Changements de contenu des éléments
 * notables, et les Navigations de la page. Chaque Fait part comme un événement de plugin du
 * Recording, à son instant ; l'API en apprend les Règles du projet. Jamais de texte ni de valeur
 * saisie : seulement des Signatures et des chemins.
 *
 * Enregistré pendant les sessions complètes seulement, jamais dans le tampon des Extraits.
 */

/** Éléments notables examinés au plus par nœud ajouté ou modifié (coût borné). */
const MAX_EXAMINES = 50
/** Éléments notables dont la visibilité est relevée au démarrage, au plus. */
const MAX_ETAT_INITIAL = 2_000
/** Un Changement de contenu par élément au plus, sur cette durée. */
const INTERVALLE_CONTENU_MS = 1_000
/** Attributs qui peuvent rendre un élément visible ou le masquer. */
const ATTRIBUTS_VISIBILITE = ['hidden', 'style', 'class', 'open']

/** L'élément et ses descendants notables candidats, au plus `MAX_EXAMINES`. */
function candidats(racine: Element): Element[] {
  const elements = [racine, ...racine.querySelectorAll(CANDIDATS_NOTABLES)]
  return elements.slice(0, MAX_EXAMINES)
}

/** L'élément est-il affiché ? (`hidden`, `display: none`, `dialog` fermé, `visibility: hidden`) */
function estVisible(element: Element, win: Window): boolean {
  if (!element.isConnected) return false
  for (let current: Element | null = element; current !== null; current = current.parentElement) {
    if (current.hasAttribute('hidden')) return false
    if (current.tagName === 'DIALOG' && !current.hasAttribute('open')) return false
    if (win.getComputedStyle(current).display === 'none') return false
  }
  return win.getComputedStyle(element).visibility !== 'hidden'
}

/** Chemin de la page : le hash n'en fait partie que s'il ressemble à une route (`#/…`). */
function cheminDe(location: Location): string {
  return location.pathname + (location.hash.startsWith('#/') ? location.hash : '')
}

export function interactionsPlugin() {
  return {
    name: INTERACTIONS_PLUGIN,
    options: {},
    observer(cb: (payload: FaitInteraction) => void, win: Window) {
      const document = win.document
      const bloque = (element: Element) => element.closest(`.${BLOCK_CLASS}`) !== null

      // Actions : phase de capture, relevées même si l'application arrête la propagation.
      const onClick = (event: Event) => {
        const element = elementActionnable(event.target)
        const cible = element === null ? null : signatureDe(element)
        if (cible !== null) cb({ type: 'action', geste: 'clic', cible })
      }
      const onSubmit = (event: Event) => {
        const cible = event.target instanceof Element ? signatureDe(event.target) : null
        if (cible !== null) cb({ type: 'action', geste: 'envoi', cible })
      }
      document.addEventListener('click', onClick, true)
      document.addEventListener('submit', onSubmit, true)

      // Apparitions : passage de caché (ou absent) à visible d'un élément notable.
      const visibles = new WeakMap<Element, boolean>()
      const notables = [...document.querySelectorAll(CANDIDATS_NOTABLES)].slice(0, MAX_ETAT_INITIAL)
      for (const element of notables) {
        if (estNotable(element)) visibles.set(element, estVisible(element, win))
      }
      const examiner = (element: Element) => {
        if (!estNotable(element)) return
        const visible = estVisible(element, win)
        const avant = visibles.get(element) ?? false
        visibles.set(element, visible)
        if (!visible || avant || bloque(element)) return
        const cible = signatureDe(element)
        if (cible !== null) cb({ type: 'apparition', cible })
      }

      // Changements de contenu : texte ou enfants modifiés, rapportés à l'élément notable le plus
      // proche, au plus un par élément et par seconde.
      const derniersContenus = new WeakMap<Element, number>()
      const contenu = (node: Node) => {
        const element = notableLePlusProche(node)
        if (element === null || bloque(element)) return
        const now = Date.now()
        const dernier = derniersContenus.get(element)
        if (dernier !== undefined && now - dernier < INTERVALLE_CONTENU_MS) return
        derniersContenus.set(element, now)
        const cible = signatureDe(element)
        if (cible !== null) cb({ type: 'contenu', cible })
      }

      // Constructeur de la fenêtre enregistrée (la page, ou une iframe de même origine).
      const page = win as Window & typeof globalThis
      const observer = new page.MutationObserver((records) => {
        for (const record of records) {
          if (record.type === 'attributes') {
            if (record.target instanceof Element) candidats(record.target).forEach(examiner)
            continue
          }
          if (record.type === 'childList') {
            for (const node of record.removedNodes) {
              if (node instanceof Element)
                for (const element of candidats(node)) visibles.set(element, false)
            }
            for (const node of record.addedNodes) {
              if (node instanceof Element) candidats(node).forEach(examiner)
            }
          }
          contenu(record.target)
        }
      })
      observer.observe(document, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ATTRIBUTS_VISIBILITE,
      })

      // Navigations : chemin de la page après pushState, replaceState, popstate ou hashchange.
      let dernierChemin = cheminDe(win.location)
      const naviguer = () => {
        const chemin = cheminDe(win.location)
        if (chemin === dernierChemin) return
        dernierChemin = chemin
        cb({ type: 'navigation', chemin })
      }
      const history = win.history
      const { pushState, replaceState } = history
      const pushStateSuivi: History['pushState'] = function (this: History, ...args) {
        pushState.apply(this, args)
        naviguer()
      }
      const replaceStateSuivi: History['replaceState'] = function (this: History, ...args) {
        replaceState.apply(this, args)
        naviguer()
      }
      history.pushState = pushStateSuivi
      history.replaceState = replaceStateSuivi
      win.addEventListener('popstate', naviguer)
      win.addEventListener('hashchange', naviguer)

      return () => {
        document.removeEventListener('click', onClick, true)
        document.removeEventListener('submit', onSubmit, true)
        observer.disconnect()
        // Rétablies seulement si personne ne les a remplacées depuis.
        if (history.pushState === pushStateSuivi) history.pushState = pushState
        if (history.replaceState === replaceStateSuivi) history.replaceState = replaceState
        win.removeEventListener('popstate', naviguer)
        win.removeEventListener('hashchange', naviguer)
      }
    },
  }
}
